import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Progress } from "../../components/ui/progress";
import { 
  ScrollText, Users, ArrowLeftRight, UserCheck, Shield, Gavel, Euro,
  RefreshCw, Plus, ChevronRight, Calendar, FileText, CheckCircle2, 
  AlertCircle, Clock, Loader2, ExternalLink, Trash2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const ICON_MAP = {
  ScrollText, Users, ArrowLeftRight, UserCheck, Shield, Gavel, Euro
};

// Color mapping
const COLOR_MAP = {
  purple: "bg-purple-100 text-purple-600",
  blue: "bg-blue-100 text-blue-600",
  indigo: "bg-indigo-100 text-indigo-600",
  green: "bg-green-100 text-green-600",
  orange: "bg-orange-100 text-orange-600",
  red: "bg-red-100 text-red-600",
  emerald: "bg-emerald-100 text-emerald-600"
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Europe/Paris'
  });
};

export default function RegistresLegaux() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [selectedRegister, setSelectedRegister] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  
  // Assistant guidé
  const [showAssistant, setShowAssistant] = useState(false);
  const [assistantStep, setAssistantStep] = useState(0);
  const [currentRegisterToUpdate, setCurrentRegisterToUpdate] = useState(null);
  
  // Modal states
  const [showAddEntryModal, setShowAddEntryModal] = useState(false);
  const [newEntry, setNewEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    title: "",
    description: ""
  });

  // Fetch dashboard
  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/corporate/registers/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(res.data);
    } catch (error) {
      console.error("Error fetching registers dashboard:", error);
      toast.error("Erreur lors du chargement des registres");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch entries for selected register
  const fetchEntries = useCallback(async (registerType) => {
    setLoadingEntries(true);
    try {
      const res = await axios.get(`${API}/api/corporate/registers/${registerType}/entries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(res.data.entries || []);
    } catch (error) {
      console.error("Error fetching entries:", error);
      toast.error("Erreur lors du chargement des entrées");
    } finally {
      setLoadingEntries(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (selectedRegister) {
      fetchEntries(selectedRegister.id);
    }
  }, [selectedRegister, fetchEntries]);

  // Sync all registers
  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/api/corporate/registers/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${res.data.total_synced} entrées synchronisées`);
      fetchDashboard();
      
      if (selectedRegister) {
        fetchEntries(selectedRegister.id);
      }
    } catch (error) {
      console.error("Error syncing registers:", error);
      toast.error("Erreur lors de la synchronisation");
    } finally {
      setSyncing(false);
    }
  };

  // Sync single register
  const handleSyncRegister = async (registerType) => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/api/corporate/registers/${registerType}/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${res.data.synced} entrées synchronisées`);
      fetchDashboard();
      fetchEntries(registerType);
    } catch (error) {
      console.error("Error syncing register:", error);
      toast.error("Erreur lors de la synchronisation");
    } finally {
      setSyncing(false);
    }
  };

  // Add manual entry
  const handleAddEntry = async () => {
    if (!newEntry.title.trim()) {
      toast.error("Veuillez entrer un titre");
      return;
    }

    try {
      await axios.post(`${API}/api/corporate/registers/${selectedRegister.id}/entries`, {
        register_type: selectedRegister.id,
        ...newEntry
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Entrée ajoutée");
      setShowAddEntryModal(false);
      setNewEntry({ date: new Date().toISOString().split('T')[0], title: "", description: "" });
      fetchEntries(selectedRegister.id);
      fetchDashboard();
    } catch (error) {
      console.error("Error adding entry:", error);
      toast.error("Erreur lors de l'ajout");
    }
  };

  // Delete entry
  const handleDeleteEntry = async (entryId) => {
    if (!confirm("Supprimer cette entrée ?")) return;

    try {
      await axios.delete(`${API}/api/corporate/registers/${selectedRegister.id}/entries/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Entrée supprimée");
      fetchEntries(selectedRegister.id);
      fetchDashboard();
    } catch (error) {
      console.error("Error deleting entry:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const getIcon = (iconName) => {
    const Icon = ICON_MAP[iconName] || FileText;
    return Icon;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-indigo-100 rounded-xl">
            <ScrollText className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Registres Légaux</h2>
            <p className="text-sm text-gray-500">
              {dashboard?.stats?.up_to_date || 0}/{dashboard?.stats?.required_count || 0} registres à jour
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          {/* Bouton Assistant Guidé */}
          {dashboard?.stats?.up_to_date < dashboard?.stats?.required_count && (
            <Button 
              onClick={() => {
                const registersToUpdate = dashboard?.registers?.filter(r => !r.is_up_to_date) || [];
                if (registersToUpdate.length > 0) {
                  setCurrentRegisterToUpdate(registersToUpdate[0]);
                  setAssistantStep(0);
                  setShowAssistant(true);
                }
              }}
              variant="outline"
              className="gap-2 border-orange-300 text-orange-700 hover:bg-orange-50"
            >
              <Gavel className="w-4 h-4" />
              Assistant guidé
            </Button>
          )}
          
          <Button 
            onClick={handleSyncAll}
            disabled={syncing}
            className="gap-2"
            data-testid="sync-all-btn"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
          Synchroniser tout
        </Button>
        </div>
      </div>

      {/* Assistant Guidé Modal */}
      <Dialog open={showAssistant} onOpenChange={setShowAssistant}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gavel className="w-5 h-5 text-indigo-600" />
              Assistant de mise à jour
            </DialogTitle>
            <DialogDescription>
              Mise à jour guidée de vos registres légaux
            </DialogDescription>
          </DialogHeader>
          
          {currentRegisterToUpdate && (
            <div className="space-y-4">
              {/* Progress */}
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>Registre {assistantStep + 1} sur {dashboard?.registers?.filter(r => !r.is_up_to_date).length || 0}</span>
              </div>
              
              {/* Current Register Info */}
              <Card className="border-indigo-200 bg-indigo-50">
                <CardContent className="p-4">
                  <h4 className="font-semibold text-indigo-900">{currentRegisterToUpdate.name}</h4>
                  <p className="text-sm text-indigo-700 mt-1">{currentRegisterToUpdate.description}</p>
                  
                  {/* Explication du registre */}
                  <div className="mt-3 p-3 bg-white rounded-lg border border-indigo-200">
                    <p className="text-xs font-medium text-gray-600 mb-1">Pourquoi ce registre est obligatoire ?</p>
                    <p className="text-xs text-gray-500">
                      {currentRegisterToUpdate.id === "registre_associes" && "L'article L. 221-3 du Code de commerce impose la tenue d'un registre des associés pour toute société."}
                      {currentRegisterToUpdate.id === "registre_mouvements_titres" && "Ce registre trace toutes les cessions et acquisitions de parts/actions (art. L. 228-1 C. com.)."}
                      {currentRegisterToUpdate.id === "registre_decisions" && "Les décisions de l'associé unique doivent être consignées (art. L. 227-9 C. com. pour les SAS)."}
                      {currentRegisterToUpdate.id === "registre_beneficiaires_effectifs" && "Obligatoire depuis 2017 pour lutter contre le blanchiment (art. L. 561-2-2 du CMF)."}
                      {currentRegisterToUpdate.id === "registre_personnel" && "Toute entreprise employant des salariés doit tenir un registre unique du personnel (art. L. 1221-13 C. trav.)."}
                      {!["registre_associes", "registre_mouvements_titres", "registre_decisions", "registre_beneficiaires_effectifs", "registre_personnel"].includes(currentRegisterToUpdate.id) && "Ce registre est requis pour assurer la conformité légale de votre société."}
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              {/* Actions */}
              <div className="space-y-2">
                <Button 
                  onClick={() => {
                    setSelectedRegister(currentRegisterToUpdate);
                    fetchEntries(currentRegisterToUpdate.id);
                    setShowAssistant(false);
                    setShowAddEntryModal(true);
                  }}
                  className="w-full gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter une entrée à ce registre
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={() => {
                    const registersToUpdate = dashboard?.registers?.filter(r => !r.is_up_to_date) || [];
                    const nextIndex = assistantStep + 1;
                    if (nextIndex < registersToUpdate.length) {
                      setCurrentRegisterToUpdate(registersToUpdate[nextIndex]);
                      setAssistantStep(nextIndex);
                    } else {
                      setShowAssistant(false);
                      toast.success("Tous les registres ont été parcourus !");
                    }
                  }}
                  className="w-full"
                >
                  Passer au registre suivant
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Progress */}
      {dashboard?.stats && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Conformité des registres</span>
              <span className="text-sm font-bold text-indigo-600">{dashboard.stats.completion_rate}%</span>
            </div>
            <Progress value={dashboard.stats.completion_rate} className="h-2" />
            <p className="text-xs text-gray-500 mt-2">
              {dashboard.stats.total_entries} entrées au total • Forme juridique: {dashboard.forme_juridique}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Register List */}
        <div className="lg:col-span-1 space-y-3">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
            Les 7 registres obligatoires
          </h3>
          
          {dashboard?.registers?.map((register) => {
            const Icon = getIcon(register.icon);
            const colorClass = COLOR_MAP[register.color] || COLOR_MAP.blue;
            const isSelected = selectedRegister?.id === register.id;
            
            return (
              <Card 
                key={register.id}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  isSelected ? 'ring-2 ring-indigo-500 border-indigo-200' : ''
                } ${!register.is_required ? 'opacity-60' : ''}`}
                onClick={() => setSelectedRegister(register)}
                data-testid={`register-${register.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${colorClass}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900 truncate">{register.name}</p>
                        {!register.is_required && (
                          <Badge variant="outline" className="text-xs">Optionnel</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{register.entry_count} entrées</span>
                        {register.is_up_to_date ? (
                          <Badge className="bg-green-100 text-green-700 text-xs gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            À jour
                          </Badge>
                        ) : register.is_required ? (
                          <Badge variant="outline" className="text-orange-600 text-xs gap-1">
                            <AlertCircle className="w-3 h-3" />
                            À mettre à jour
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Selected Register Details */}
        <div className="lg:col-span-2">
          {selectedRegister ? (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {(() => {
                      const Icon = getIcon(selectedRegister.icon);
                      return <Icon className="w-5 h-5 text-gray-500" />;
                    })()}
                    {selectedRegister.name}
                  </CardTitle>
                  <CardDescription>
                    {dashboard?.registers?.find(r => r.id === selectedRegister.id)?.entry_count || 0} entrées
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSyncRegister(selectedRegister.id)}
                    disabled={syncing}
                    className="gap-2"
                  >
                    <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                    Sync
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setShowAddEntryModal(true)}
                    className="gap-2"
                    data-testid="add-entry-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Ajouter
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingEntries ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : entries.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Aucune entrée dans ce registre</p>
                    <p className="text-sm">Cliquez sur "Sync" pour importer les données automatiquement</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {entries.map((entry) => (
                      <div 
                        key={entry.id}
                        className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition-colors"
                        data-testid={`entry-${entry.id}`}
                      >
                        <div className="flex-shrink-0 w-10 h-10 bg-white rounded-lg border flex items-center justify-center">
                          <Calendar className="w-4 h-4 text-gray-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900 truncate">{entry.title}</p>
                            {entry.source !== "manual" && (
                              <Badge variant="outline" className="text-xs">
                                Auto
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 truncate">{entry.description}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(entry.date)}
                            </span>
                            {entry.reference_doc_id && (
                              <span className="flex items-center gap-1 text-blue-500">
                                <ExternalLink className="w-3 h-3" />
                                Document lié
                              </span>
                            )}
                          </div>
                        </div>
                        {entry.source === "manual" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteEntry(entry.id)}
                            className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <ScrollText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Sélectionnez un registre
                </h3>
                <p className="text-gray-500">
                  Cliquez sur un registre pour voir ses entrées et le mettre à jour
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Add Entry Modal */}
      <Dialog open={showAddEntryModal} onOpenChange={setShowAddEntryModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nouvelle entrée</DialogTitle>
            <DialogDescription>
              Ajouter une entrée manuelle au registre {selectedRegister?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="entry-date">Date</Label>
              <Input
                id="entry-date"
                type="date"
                value={newEntry.date}
                onChange={(e) => setNewEntry(prev => ({ ...prev, date: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="entry-title">Titre *</Label>
              <Input
                id="entry-title"
                value={newEntry.title}
                onChange={(e) => setNewEntry(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Ex: PV AGO 2025"
                data-testid="entry-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="entry-description">Description</Label>
              <Textarea
                id="entry-description"
                value={newEntry.description}
                onChange={(e) => setNewEntry(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Détails de l'entrée..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddEntryModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleAddEntry} data-testid="save-entry-btn">
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
